import { useEffect } from 'react';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { VendorsApiResponse, Vendor } from '../../../types/vendor-types';
import useGlynt from '../useGlynt';
import { getOffsetFromUrl } from '../../../utils/getOffsetFromUrl';

function mergeResults(data: InfiniteData<VendorsApiResponse> | undefined) {
  if (!data) return [];

  let results: Vendor[] = [];

  data.pages.map((page) => {
    results = results.concat(page.results);
    return true;
  });

  return results;
}

export default function useGetAllVendors(enabled: boolean = true) {
  const glynt = useGlynt();

  const limit = 150;

  const {
    data,
    isError,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['vendors'],
    queryFn: async ({ pageParam }) => {
      const queryParams = {
        advanced: true,
        limit,
        offset: pageParam,
      };

      return (await glynt).vendor.getVendors(queryParams);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.next) {
        const offset = getOffsetFromUrl(lastPage.next);
        return offset;
      }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
    enabled,
  });

  useEffect(() => {
    if (enabled && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [enabled, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const currentPage = data?.pages?.length ?? 0;
  const totalPages = data?.pages?.[0]?.count ? Math.ceil(data.pages[0].count / limit) : 0;

  return {
    isLoading,
    isError,
    isFetching,
    data: mergeResults(data),
    currentPage,
    totalPages,
    refetch,
  };
}
